// groupRouteAliases.tsx
//
// Maps the new Nav v2 /group/* paths to the existing pages in App.tsx.
//
// The GroupSidebar (Agent A) uses /group/* paths per the Nav v2 IA blueprint,
// but our existing pages still live at the original routes (/dashboard,
// /projects, /tenders, etc).
//
// To make the sidebar links work WITHOUT moving every page, we register
// /group/* paths as route aliases that render the same component as the
// original route.
//
// Strategy:
//   - Group routes that have an existing page → alias to that page
//   - Group routes that have no existing page → redirect to closest match
//   - Routes the user has flagged as "coming soon" stay as Navigate placeholders
//
// Usage in App.tsx:
//   import { renderGroupRouteAliases } from '@/routes/groupRouteAliases'
//   ...
//   {renderGroupRouteAliases(isAuthenticated)}

import { ReactNode, lazy } from 'react'
import { Route, Navigate } from 'react-router-dom'

// Existing pages reused for /group/* aliases.
// All use the lazy() pattern to match the existing App.tsx style.
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then(m => ({ default: m.default || m })))
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage'))
const TendersPage = lazy(() => import('@/pages/TendersPage'))
const ClientsPage = lazy(() => import('@/pages/ClientsPage'))
const ConsultantsPage = lazy(() => import('@/pages/ConsultantsPage'))
const SuppliersPage = lazy(() => import('@/pages/SuppliersPage'))
const SubcontractorsListPage = lazy(() => import('@/pages/SubcontractorsListPage'))
const ReportsPage = lazy(() => import('@/pages/ReportsPage'))
const CashFlowDashboardPage = lazy(() => import('@/pages/CashFlowDashboardPage'))
const AgingReportPage = lazy(() => import('@/pages/AgingReportPage'))
const VATConfigPage = lazy(() => import('@/pages/VATConfigPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const EmployeesListPage = lazy(() => import('@/pages/EmployeesListPage'))
const DepartmentsTreePage = lazy(() => import('@/pages/DepartmentsTreePage'))
const AttendanceDashboardPage = lazy(() => import('@/pages/AttendanceDashboardPage'))
const LeaveRequestsPage = lazy(() => import('@/pages/LeaveRequestsPage'))
const PayrollPeriodsPage = lazy(() => import('@/pages/PayrollPeriodsPage'))
const GratuityCalculatorPage = lazy(() => import('@/pages/GratuityCalculatorPage'))
const ChatSessionsPage = lazy(() => import('@/pages/ChatSessionsPage'))
const PredictiveDashboardPage = lazy(() => import('@/pages/PredictiveDashboardPage'))
const PredictiveAlertsPage = lazy(() => import('@/pages/PredictiveAlertsPage'))
const InvoiceScansListPage = lazy(() => import('@/pages/InvoiceScansListPage'))
const ProcurementAnalyticsPage = lazy(() => import('@/pages/ProcurementAnalyticsPage'))
const InvoiceScanStatsPage = lazy(() => import('@/pages/InvoiceScanStatsPage'))
const TaxAuthorityFilingPage = lazy(() => import('@/pages/TaxAuthorityFilingPage'))
const MOLLaborCompliancePage = lazy(() => import('@/pages/MOLLaborCompliancePage'))
const EmiratesIDVerificationPage = lazy(() => import('@/pages/EmiratesIDVerificationPage'))
const GovtComplianceDashboardPage = lazy(() => import('@/pages/govt/ComplianceDashboardPage'))
const ApprovalsInboxPage = lazy(() => import('@/pages/ApprovalsInboxPage'))
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'))
const MaterialCatalogPage = lazy(() => import('@/pages/MaterialCatalogPage'))
const HijriCalendarPage = lazy(() => import('@/pages/HijriCalendarPage'))
const LocaleSettingsPage = lazy(() => import('@/pages/LocaleSettingsPage'))
const CurrencyConversionsPage = lazy(() => import('@/pages/CurrencyConversionsPage'))
const RBACSecurityDashboardPage = lazy(() => import('@/pages/RBACSecurityDashboardPage'))
// === ACCOUNTING STATEMENTS + PERIODS (#488 statements + #25 periods) BEGIN ===
const TrialBalancePage = lazy(() => import('@/pages/accounting/TrialBalancePage'))
const ChartOfAccountsPage = lazy(() => import('@/pages/accounting/ChartOfAccountsPage'))
const AccountLedgerPage = lazy(() => import('@/pages/accounting/AccountLedgerPage'))
const IncomeStatementPage = lazy(() => import('@/pages/accounting/IncomeStatementPage'))
const BalanceSheetPage = lazy(() => import('@/pages/accounting/BalanceSheetPage'))
const CashFlowStatementPage = lazy(() => import('@/pages/accounting/CashFlowStatementPage'))
const ProjectPnLPage = lazy(() => import('@/pages/accounting/ProjectPnLPage'))
const AccountingAgingPage = lazy(() => import('@/pages/accounting/AccountingAgingPage'))
const BudgetVariancePage = lazy(() => import('@/pages/accounting/BudgetVariancePage'))
const AccountingPeriodsPage = lazy(() => import('@/pages/accounting/AccountingPeriodsPage'))
const JournalEntryPage = lazy(() => import('@/pages/accounting/JournalEntryPage'))
// === ACCOUNTING STATEMENTS + PERIODS END ===
// === FIXED ASSETS (WAVE12-4) BEGIN ===
const FixedAssetsPage = lazy(() => import('@/pages/fixed-assets/FixedAssetsPage'))
const FixedAssetDetailsPage = lazy(() => import('@/pages/fixed-assets/FixedAssetDetailsPage'))
const DepreciationRunsPage = lazy(() => import('@/pages/fixed-assets/DepreciationRunsPage'))
// === FIXED ASSETS (WAVE12-4) END ===
// === B29 LANE D (PROCUREMENT-GROUP-NAV-01) BEGIN ===
// Group-level Procurement, i.e. tenant-wide and NOT scoped to one project.
// PurchaseOrdersPage and SupplierInvoicesPage read useRouteProjectId(), which
// returns undefined outside /projects/:projectId/*, so both send no project_id
// here and stay tenant-wide. ProcurementDashboard, RFQsPage and SuppliersPage
// never read a project id at all -- they are tenant-wide by construction.
const ProcurementDashboardPage = lazy(() => import('@/pages/Procurement/ProcurementDashboard'))
const PurchaseOrdersPage = lazy(() => import('@/pages/PurchaseOrdersPage'))
// B31 Lane L4 / PO-GROUP-SCOPE-01: the PO detail page is tenant-wide too -- it
// resolves the PO by id and never reads a project id -- so it belongs at group
// level next to the list.
const PurchaseOrderDetailsPage = lazy(() => import('@/pages/PurchaseOrderDetailsPage'))
// SI-DETAIL-GROUP-ROUTE-01 (B31 Lane L2)
const InvoiceDetailsPage = lazy(() => import('@/pages/InvoiceDetailsPage'))
const SupplierInvoicesPage = lazy(() => import('@/pages/SupplierInvoicesPage'))
// GRN-NAV-ROUTE-01: the API-backed GRNs page (procurementApi), the same
// component projectRouteAliases.tsx mounts at .../procurement/receipts.
const ProcurementGRNsPage = lazy(() => import('@/pages/Procurement/GRNsPage'))
const ProcurementRFQsPage = lazy(() => import('@/pages/Procurement/RFQsPage'))
// RFQ-CREATE-ROUTE-01 / RFQ-DETAIL-GROUP-ROUTE-01: the RFQ create form and the
// RFQ detail page are tenant-wide too (an RFQ's project_id is optional --
// api/procurement.py:136), so both belong at group level next to the list.
const ProcurementRFQCreatePage = lazy(() => import('@/pages/Procurement/RFQCreatePage'))
const ProcurementRFQDetailPage = lazy(() => import('@/pages/Procurement/RFQDetailPage'))
// === B29 LANE D END ===
// === AGENT_W3_L (Tender Bidding) BEGIN ===
const TenderBiddingDashboardPage = lazy(() => import('@/pages/TenderBidding'))
const TenderBiddingStrategyPage = lazy(() => import('@/pages/TenderBidding/strategy'))
const TenderBiddingBondsPage = lazy(() => import('@/pages/TenderBidding/bonds'))
const TenderBiddingPQPage = lazy(() => import('@/pages/TenderBidding/prequalifications'))
// === AGENT_W3_L (Tender Bidding) END ===

// Type for the alias map
interface RouteAlias {
  path: string
  element: ReactNode
}

/**
 * Build the list of route aliases for /group/* paths.
 * Each alias renders an existing page when authenticated.
 */
export function buildGroupRouteAliases(isAuthenticated: boolean): RouteAlias[] {
  const protect = (Page: React.ComponentType): ReactNode =>
    isAuthenticated ? <Page /> : <Navigate to="/login" replace />

  return [
    // Group home
    { path: '/group/home', element: protect(DashboardPage) },

    // Projects
    { path: '/group/projects', element: protect(ProjectsPage) },

    // Tenders
    { path: '/group/tenders', element: protect(TendersPage) },

    // === AGENT_W3_L (Tender Bidding) BEGIN ===
    { path: '/group/bidding', element: protect(TenderBiddingDashboardPage) },
    { path: '/group/bidding/strategy', element: protect(TenderBiddingStrategyPage) },
    { path: '/group/bidding/bonds', element: protect(TenderBiddingBondsPage) },
    { path: '/group/bidding/prequalifications', element: protect(TenderBiddingPQPage) },
    // === AGENT_W3_L (Tender Bidding) END ===

    // Directory
    { path: '/group/companies/clients', element: protect(ClientsPage) },
    { path: '/group/companies/consultants', element: protect(ConsultantsPage) },
    { path: '/group/companies/suppliers', element: protect(SuppliersPage) },
    { path: '/group/companies/subcontractors', element: protect(SubcontractorsListPage) },

    // Accounting
    { path: '/group/accounting/reports', element: protect(ReportsPage) },
    { path: '/group/accounting/cash-flow', element: protect(CashFlowDashboardPage) },
    { path: '/group/accounting/aging', element: protect(AgingReportPage) },
    { path: '/group/accounting/taxes', element: protect(VATConfigPage) },
    { path: '/group/accounting/invoice-scan', element: protect(InvoiceScansListPage) },
    { path: '/group/accounting/procurement', element: protect(ProcurementAnalyticsPage) },
// === ACCOUNTING STATEMENTS + PERIODS (#488 + #25) -- world-class GL screens ===
    { path: '/group/accounting/statements/trial-balance', element: protect(TrialBalancePage) },
    { path: '/group/accounting/statements/chart-of-accounts', element: protect(ChartOfAccountsPage) },
    { path: '/group/accounting/statements/general-ledger', element: protect(AccountLedgerPage) },
    { path: '/group/accounting/statements/income', element: protect(IncomeStatementPage) },
    { path: '/group/accounting/statements/balance-sheet', element: protect(BalanceSheetPage) },
    { path: '/group/accounting/statements/cash-flow', element: protect(CashFlowStatementPage) },
    { path: '/group/accounting/statements/project-pnl', element: protect(ProjectPnLPage) },
    { path: '/group/accounting/statements/aging', element: protect(AccountingAgingPage) },
    { path: '/group/accounting/statements/budget-variance', element: protect(BudgetVariancePage) },
    { path: '/group/accounting/periods', element: protect(AccountingPeriodsPage) },
    { path: '/group/accounting/journal-entry', element: protect(JournalEntryPage) },
    // === FIXED ASSETS (WAVE12-4) — register + schedule + depreciation runs ===
    // Static segment before the :assetId param (React Router v6 ranks by
    // specificity, but keep the order explicit).
    { path: '/group/accounting/fixed-assets', element: protect(FixedAssetsPage) },
    { path: '/group/accounting/fixed-assets/depreciation-runs', element: protect(DepreciationRunsPage) },
    { path: '/group/accounting/fixed-assets/:assetId', element: protect(FixedAssetDetailsPage) },

    // === B29 LANE D (PROCUREMENT-GROUP-NAV-01) BEGIN ===
    // Accountants and procurement staff work ACROSS projects: before this
    // block the only way to reach a supplier invoice or a PO was to enter a
    // single project first. These aliases expose the same pages at group
    // level; useRouteProjectId() yields undefined here so each list stays
    // tenant-wide instead of silently scoping to one project.
    { path: '/group/procurement', element: protect(ProcurementDashboardPage) },
    { path: '/group/procurement/pos', element: protect(PurchaseOrdersPage) },
    // === B31 LANE L4 (PO-GROUP-SCOPE-01 / DEAD-CREATE-ROUTES-01) BEGIN ===
    // Before these two, `/group/procurement/pos` was the ONLY PO entry at group
    // level: the list's row click went to the absolute `/purchase-orders/:id`,
    // which teleported the user out of the group scope with no way back. The
    // list now builds its navigation from the live pathname
    // (pages/Procurement/procurementScope.ts), so the targets have to exist.
    // `new` is declared before `:poId` per house style; router v7 ranks static
    // above dynamic regardless of order.
    { path: '/group/procurement/pos/new', element: protect(PurchaseOrdersPage) },
    // The detail page reads `:poId` (PurchaseOrderDetailsPage.tsx:62), so the
    // alias declares the SAME param name — `:id` here would reproduce the
    // Number(undefined) -> NaN -> "PO not found" bug at group level.
    { path: '/group/procurement/pos/:poId', element: protect(PurchaseOrderDetailsPage) },
    // === B31 LANE L4 END ===
    { path: '/group/procurement/supplier-invoices', element: protect(SupplierInvoicesPage) },
    // === B31 LANE L2 (SI-DETAIL-GROUP-ROUTE-01) BEGIN ===
    // DEAD-CREATE-ROUTES-01: group twin of /supplier-invoices/new. It now sits
    // ALONGSIDE a `:invoiceId` alias, so `new` could be swallowed by it —
    // router v7 ranks static above dynamic regardless of declaration order, and
    // `new` is declared first per house style, so it still wins. The negative
    // test in procurementRouteAliases.test.tsx pins that.
    { path: '/group/procurement/supplier-invoices/new', element: protect(SupplierInvoicesPage) },
    // `:invoiceId` is the param name InvoiceDetailsPage.tsx:266 reads. `:id`
    // here would reproduce the UAT-ACC-P1-05a Number(undefined) -> NaN ->
    // "Invoice not found" bug at group level (same class as PO-GROUP-SCOPE-01).
    { path: '/group/procurement/supplier-invoices/:invoiceId', element: protect(InvoiceDetailsPage) },
    // === B31 LANE L2 END ===
    { path: '/group/procurement/rfqs', element: protect(ProcurementRFQsPage) },
    // RFQ-CREATE-ROUTE-01: before this, `/group/procurement/rfqs/new` matched
    // NOTHING (no group :rfqId alias, no catch-all route in the app), and the
    // list page's "New RFQ" button navigated to the ABSOLUTE root path, which
    // fell through to /procurement/rfqs/:rfqId -> "Invalid RFQ ID".
    // `new` is declared before `:rfqId` per house style; router v7 ranks
    // static above dynamic regardless of order.
    { path: '/group/procurement/rfqs/new', element: protect(ProcurementRFQCreatePage) },
    // RFQ-DETAIL-GROUP-ROUTE-01: the detail page reads `:rfqId`
    // (RFQDetailPage.tsx:21), so the alias declares the SAME param name and the
    // page needs no change to work at group level.
    { path: '/group/procurement/rfqs/:rfqId', element: protect(ProcurementRFQDetailPage) },
    // Suppliers is deliberately reachable from BOTH the Directory group and
    // Procurement -- /group/companies/suppliers above is untouched.
    { path: '/group/procurement/suppliers', element: protect(SuppliersPage) },
    // === B32 LANE M3 (GRN-NAV-ROUTE-01) BEGIN ===
    // /group/procurement/receipts. This slot held a comment explaining why the
    // entry could NOT exist: `GET /procurement/grns` declared `po_id` as
    // REQUIRED, so the only GRN read available was a PO lookup and there was no
    // list for a group route to render (GRN-LIST-SCOPE-01). That reason is gone
    // -- `po_id` is now one optional filter on a tenant-scoped, paginated list
    // (backend api/procurement.py `list_grns` + GRNService.search,
    // GRN-LIST-REQUIRES-PO-01), so the entry lands here like every other
    // procurement page.
    //
    // The two other obstacles that comment named are also answered, not
    // side-stepped: `GET /material/grn` is still gated on the unrelated
    // `material.grn.view` and is still NOT what this renders, and
    // goods_received_notes still has no project_id -- the project filter is a
    // join onto PurchaseOrder.project_id inside the service.
    //
    // `receipts`, not `grns`: it matches the project route that already shipped
    // (projectRouteAliases.tsx, ROUTE-RECEIPTS-GRN-01), the 'Material Receipts'
    // breadcrumb segment (Breadcrumb/routeRegistry.ts) and the sidebar label.
    // pages/Procurement/procurementScope.ts `grnBasePath` builds exactly this.
    { path: '/group/procurement/receipts', element: protect(ProcurementGRNsPage) },
    // === B32 LANE M3 END ===
    // === B29 LANE D END ===

    // HR
    { path: '/group/hr/employees', element: protect(EmployeesListPage) },
    { path: '/group/hr/departments', element: protect(DepartmentsTreePage) },
    { path: '/group/hr/attendance', element: protect(AttendanceDashboardPage) },
    { path: '/group/hr/leaves', element: protect(LeaveRequestsPage) },
    { path: '/group/hr/payroll', element: protect(PayrollPeriodsPage) },
    { path: '/group/hr/eos', element: protect(GratuityCalculatorPage) },
    { path: '/group/hr/allocation', element: protect(EmployeesListPage) },

    // Materials (cross-project catalog only)
    { path: '/group/materials/catalog', element: protect(MaterialCatalogPage) },

    // ORKSTRA AI
    { path: '/group/ai/assistant', element: protect(ChatSessionsPage) },
    { path: '/group/ai/predictive', element: protect(PredictiveDashboardPage) },
    { path: '/group/ai/alerts', element: protect(PredictiveAlertsPage) },
    { path: '/group/ai/usage', element: protect(InvoiceScanStatsPage) },

    // Compliance
    { path: '/group/compliance/emaratax', element: protect(TaxAuthorityFilingPage) },
    { path: '/group/compliance/mohre', element: protect(MOLLaborCompliancePage) },
    { path: '/group/compliance/gdrfa', element: protect(EmiratesIDVerificationPage) },
    { path: '/group/compliance/id-verify', element: protect(EmiratesIDVerificationPage) },
    { path: '/group/compliance/dashboard', element: protect(GovtComplianceDashboardPage) },
    { path: '/group/compliance/certificates', element: protect(GovtComplianceDashboardPage) },

    // Approvals
    { path: '/group/approvals', element: protect(ApprovalsInboxPage) },

    // Notifications
    { path: '/group/notifications', element: protect(NotificationsPage) },

    // Settings (group with sub-items)
    { path: '/group/settings', element: protect(SettingsPage) },
    { path: '/group/settings/hijri', element: protect(HijriCalendarPage) },
    { path: '/group/settings/locale', element: protect(LocaleSettingsPage) },
    { path: '/group/settings/currency', element: protect(CurrencyConversionsPage) },
    { path: '/group/settings/security', element: protect(RBACSecurityDashboardPage) },
  ]
}

/**
 * Render all /group/* alias routes as <Route> elements.
 * Drop into the <Routes> tree in App.tsx.
 */
export function renderGroupRouteAliases(isAuthenticated: boolean): ReactNode {
  const aliases = buildGroupRouteAliases(isAuthenticated)
  return aliases.map(({ path, element }) => (
    <Route key={path} path={path} element={element} />
  ))
}
